import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@/hooks/useTranslation";
import { debounce } from "lodash";
import { useForm, Control } from "react-hook-form";
import useJwt from "@/components/checkout/hooks/useJwt";
import {
  listUserCompanies,
  UserCompany,
} from "@/networking/user-companies.api";
import DropdownSelect from "@/components/ui/input/dropdown-select";
import { FLUID_CDN_URL } from "@/lib/constants";

export interface VolumeRep {
  id: number;
  full_name: string;
  email: string;
  image_url?: string;
}

interface RepSearchProps {
  selectedRep: VolumeRep | null;
  onSelect: (rep: UserCompany) => void;
  disabled?: boolean;
  title?: string;
  customerId?: number;
  jwtToken?: string | null;
  isAccordionOpen?: boolean;
}

interface RepSearchForm {
  repSearch: string;
}

const REPS_QUERY_KEY = "SearchReps";

export default function RepSearch({
  onSelect,
  disabled = false,
  jwtToken,
  isAccordionOpen = false,
}: RepSearchProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const queryClient = useQueryClient();
  const { jwt } = useJwt();

  const { control } = useForm<RepSearchForm>({
    defaultValues: {
      repSearch: "",
    },
  });

  const {
    data: userCompaniesData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [REPS_QUERY_KEY, searchTerm, jwt],
    queryFn: async ({ queryKey }) => {
      const currentSearchTerm = queryKey[1] as string;
      if (!currentSearchTerm || currentSearchTerm.trim().length < 1) {
        return { user_companies: [] };
      }

      const token = jwt;
      if (!token) {
        console.warn("No JWT provided for rep search");
        return { user_companies: [] };
      }

      return listUserCompanies({
        page: 1,
        per_page: 20,
        search_query: currentSearchTerm,
        jwt: jwt,
      });
    },
    enabled: inputValue.trim().length > 0 && !!(jwt || jwtToken),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        const trimmed = value.trim();
        setSearchTerm(trimmed);
      }, 350),
    [],
  );

  const handleInputChange = useCallback(
    (option: { id: string | number; name: string }) => {
      // This will be called when user selects an option
      if (disabled) return;

      const rep = userCompaniesData?.user_companies?.find(
        (r) => r.id === option.id,
      );

      if (rep) {
        onSelect(rep);
        // Clear the search
        setSearchTerm("");
        queryClient.setQueryData([REPS_QUERY_KEY, ""], { reps: [] });
      }
    },
    [disabled, userCompaniesData?.user_companies, onSelect, queryClient],
  );

  const handleChange = useCallback(() => {
    // This will be called when user types, triggering the search
    const currentValue = control._formValues.repSearch;
    if (currentValue !== undefined) {
      setInputValue(currentValue);
      debouncedSetSearchTerm(currentValue);
    }
  }, [control._formValues.repSearch, debouncedSetSearchTerm]);

  return (
    <div className={`w-full ${isAccordionOpen ? "z-50" : "z-10"}`}>
      <div className="mb-2 text-sm font-medium text-gray-900">{t("Rep")}</div>
      <div className="relative">
        <DropdownSelect
          control={control as Control<RepSearchForm>}
          name="repSearch"
          label={""}
          placeholder={t("search_for_rep_by_name_or_email")}
          placeholderIcon={{
            src: `${FLUID_CDN_URL}/images/checkout/icons/magnifying-glass.svg`,
            alt: t("search"),
            width: 16,
            height: 16,
          }}
          options={(userCompaniesData?.user_companies || []).map((rep) => ({
            id: rep.id,
            name: `${
              `${rep.user.first_name} ${rep.user.last_name}`.trim() ||
              rep.user.email ||
              `${t("representative")} ${rep.id}`
            }\n${rep.user.email || ""}`,
          }))}
          id="rep-search"
          containerStyles={`w-full ${isAccordionOpen ? "z-50" : "z-10"}`}
          inputStyles="pl-10 w-full pt-2 rounded"
          onInputChange={handleInputChange}
          onChange={handleChange}
          disabled={disabled}
          notFoundMessage={
            isLoading ? t("loading") : t("no_representatives_found")
          }
          disableCapitalize={true}
        />
      </div>
      {isLoading ? (
        <div className="mt-2 text-sm text-gray-500">{t("loading")}...</div>
      ) : isError && searchTerm.trim().length > 0 ? (
        <div className="mt-2 text-sm text-red-600">
          {t("error_loading_reps")}
        </div>
      ) : !isLoading &&
        !isError &&
        searchTerm.trim().length > 0 &&
        (!userCompaniesData?.user_companies ||
          userCompaniesData.user_companies.length === 0) ? (
        <div className="mt-2 text-sm text-gray-500">
          {t("no_representatives_found")}
        </div>
      ) : null}
    </div>
  );
}
